import { useState, useEffect } from "react";
import { supabase } from "@/lib/supabaseClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Hammer, Save, HelpCircle, MapPin, TrendingUp } from "lucide-react";
import { toast } from "sonner";

interface LaborCost {
  id: string;
  state_code: string;
  hourly_rate_mechanical: number;
  hourly_rate_bodywork: number;
  paint_material_factor: number;
  updated_at: string;
}

const BRAZIL_STATES = [
  "AC", "AL", "AP", "AM", "BA", "CE", "DF", "ES", "GO", "MA", "MT", "MS", "MG",
  "PA", "PB", "PR", "PE", "PI", "RJ", "RN", "RS", "RO", "RR", "SC", "SP", "SE", "TO"
];

export default function AdminLaborCosts() {
  const [costs, setCosts] = useState<LaborCost[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  
  // Form State
  const [stateCode, setStateCode] = useState("SP");
  const [rateMech, setRateMech] = useState(120);
  const [rateBody, setRateBody] = useState(150);
  const [paintFactor, setPaintFactor] = useState(1.1);

  useEffect(() => {
    fetchCosts();
  }, []);

  async function fetchCosts() {
    setLoading(true);
    const { data, error } = await supabase
      .from("labor_costs")
      .select("*")
      .order("state_code", { ascending: true });

    if (error) {
      console.error(error);
      toast.error("Erro ao carregar custos");
    } else {
      setCosts(data || []);
    }
    setLoading(false);
  }

  async function handleSave() {
    const payload = {
      state_code: stateCode,
      hourly_rate_mechanical: rateMech,
      hourly_rate_bodywork: rateBody,
      paint_material_factor: paintFactor,
      updated_at: new Date().toISOString(),
    };

    let error;
    if (editingId) {
      ({ error } = await supabase
        .from("labor_costs")
        .update(payload)
        .eq("id", editingId));
    } else {
      ({ error } = await supabase
        .from("labor_costs")
        .insert([payload]));
    }

    if (error) {
      console.error(error);
      toast.error("Erro ao salvar custos");
    } else {
      toast.success("Custos atualizados com sucesso");
      resetForm();
      fetchCosts();
    }
  }

  const resetForm = () => {
    setEditingId(null);
    setStateCode("SP");
    setRateMech(120);
    setRateBody(150);
    setPaintFactor(1.1);
  };

  const startEdit = (cost: LaborCost) => {
    setEditingId(cost.id);
    setStateCode(cost.state_code);
    setRateMech(cost.hourly_rate_mechanical);
    setRateBody(cost.hourly_rate_bodywork);
    setPaintFactor(cost.paint_material_factor);
  };

  return (
    <div className="space-y-8">
      <div className="flex items-center gap-3 border-b-4 border-foreground pb-4">
        <Hammer className="w-8 h-8 text-primary" />
        <h1 className="text-3xl font-heading font-black uppercase tracking-tighter">Custos de Mão de Obra</h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Editor Card */}
        <div className="lg:col-span-1">
          <Card className="border-4 border-foreground shadow-[6px_6px_0px_0px_rgba(0,0,0,1)] bg-background sticky top-6">
            <CardHeader>
                <label className="text-xs font-black uppercase tracking-widest text-muted-foreground flex items-center gap-2">
                  {editingId ? "Editar Região" : "Configurar Região"}
                  <HelpCircle className="w-4 h-4 text-muted-foreground cursor-help" />
                </label>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <label className="text-xs font-black uppercase tracking-widest text-muted-foreground flex items-center gap-1">
                  <MapPin className="w-3 h-3" /> Estado (UF)
                </label>
                <select
                  className="w-full p-2 border-2 border-foreground bg-background font-bold outline-none focus:border-primary"
                  value={stateCode}
                  onChange={(e) => setStateCode(e.target.value)}
                  disabled={!!editingId}
                >
                  {BRAZIL_STATES.map(st => (
                    <option key={st} value={st}>{st}</option>
                  ))}
                </select>
              </div>

              <div className="space-y-2">
                <label className="text-xs font-black uppercase tracking-widest text-muted-foreground">Mecânica (R$ / Hora)</label>
                <Input
                  type="number"
                  className="border-2 border-foreground font-black text-lg h-12"
                  value={rateMech}
                  onChange={(e) => setRateMech(Number(e.target.value))}
                />
              </div>

              <div className="space-y-2">
                <label className="text-xs font-black uppercase tracking-widest text-muted-foreground">Funilaria/Pintura (R$ / Hora)</label>
                <Input
                  type="number"
                  className="border-2 border-foreground font-black text-lg h-12"
                  value={rateBody}
                  onChange={(e) => setRateBody(Number(e.target.value))}
                />
              </div>

              <div className="space-y-2">
                <label className="text-xs font-black uppercase tracking-widest text-muted-foreground flex items-center gap-1">
                  <TrendingUp className="w-3 h-3 text-primary" /> Fator de Peças/Tintas
                </label>
                <Input
                  type="number"
                  step="0.01"
                  className="border-2 border-foreground font-black text-lg h-12"
                  value={paintFactor}
                  onChange={(e) => setPaintFactor(Number(e.target.value))}
                />
                <p className="text-[10px] text-muted-foreground uppercase font-mono">Multiplicador de custo de materiais (Ex: 1.1 = +10%)</p>
              </div>

              <div className="flex gap-2 pt-4">
                <Button variant="outline" onClick={resetForm} className="flex-1 font-bold border-2 border-foreground">Limpar</Button>
                <Button onClick={handleSave} className="flex-2 font-bold border-2 border-foreground shadow-[2px_2px_0px_0px_rgba(0,0,0,1)]">
                  <Save className="w-4 h-4 mr-2" /> {editingId ? "Atualizar" : "Salvar"}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* List Card */}
        <div className="lg:col-span-2 space-y-4">
          <div className="bg-background border-4 border-foreground p-4 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b-2 border-foreground">
                  <th className="text-left p-3 text-xs font-black uppercase tracking-widest text-muted-foreground">UF</th>
                  <th className="text-right p-3 text-xs font-black uppercase tracking-widest text-muted-foreground">Mecânica</th>
                  <th className="text-right p-3 text-xs font-black uppercase tracking-widest text-muted-foreground">Funilaria</th>
                  <th className="text-right p-3 text-xs font-black uppercase tracking-widest text-muted-foreground">Fator</th>
                  <th className="text-center p-3 text-xs font-black uppercase tracking-widest text-muted-foreground">Ações</th>
                </tr>
              </thead>
              <tbody>
                {costs.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="p-8 text-center font-mono uppercase text-muted-foreground italic text-sm">Nenhuma região configurada</td>
                  </tr>
                ) : (
                  costs.map((c) => (
                    <tr key={c.id} className="border-b border-muted transition-colors hover:bg-muted/5 group">
                      <td className="p-3 font-black text-xl">{c.state_code}</td>
                      <td className="p-3 text-right font-bold text-lg">R$ {c.hourly_rate_mechanical}</td>
                      <td className="p-3 text-right font-bold text-lg">R$ {c.hourly_rate_bodywork}</td>
                      <td className="p-3 text-right font-mono font-bold text-primary">{c.paint_material_factor}x</td>
                      <td className="p-3 text-center">
                        <Button variant="ghost" size="sm" onClick={() => startEdit(c)} className="opacity-0 group-hover:opacity-100 transition-opacity">
                          <Save className="w-4 h-4 mr-1 text-primary" /> Editar
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          
          <div className="bg-primary/5 border-l-4 border-primary p-4">
            <p className="text-sm font-medium leading-relaxed">
              <strong>Info:</strong> Caso um estado não esteja configurado acima, o simulador utilizará valores padrão nacionais (R$ 100/hora para mecânica e funilaria). A IA foi atualizada para indicar a fonte dos dados no relatório final.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
